import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlParams';
import { bikeVariantAPI } from '../services/api';
import './Brands.css';
import SearchableDropdown from '../components/SearchableDropdown';

function BikeVariants() {
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [previewVariants, setPreviewVariants] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingVariant, setSyncingVariant] = useState(null);
  const [selectedBrandForSync, setSelectedBrandForSync] = useState('');
  const [selectedModelForSync, setSelectedModelForSync] = useState('');
  const [modelsForSync, setModelsForSync] = useState([]);
  const [previewBikeInfo, setPreviewBikeInfo] = useState(null);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [bikeFilter, setBikeFilter] = useState('');

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [formData, setFormData] = useState({
    bikeId: '',
    name: '',
    price: '',
    image: '',
    url: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch brands for dropdown
  const fetchBrands = async () => {
    try {
      const response = await bikeVariantAPI.getBrands();
      setBrands(response.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  // Fetch models for dropdown
  const fetchModels = async (brandId) => {
    try {
      const params = brandId ? { brandId } : {};
      const response = await bikeVariantAPI.getModels(params);
      setModels(response.data || []);
    } catch (err) {
      console.error('Failed to fetch models:', err);
    }
  };

  const fetchVariants = async (currentPage, currentLimit, currentSearch, currentBikeId) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentBikeId) params.bikeId = currentBikeId;

      const response = await bikeVariantAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setVariants(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setVariants(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch bike variants');
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchModels();
  }, []);

  useEffect(() => {
    fetchVariants(page, limit, search, bikeFilter);
  }, [page, limit, search, bikeFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bike variant?')) {
      try {
        await bikeVariantAPI.delete(id);
        fetchVariants(page, limit, search, bikeFilter);
      } catch (err) {
        setError('Failed to delete bike variant');
      }
    }
  };

  // Form modal handlers
  const handleCreate = () => {
    setEditingVariant(null);
    setFormData({
      bikeId: bikeFilter || '',
      name: '',
      price: '',
      image: '',
      url: ''
    });
    setShowFormModal(true);
  };

  const handleEdit = (variant) => {
    setEditingVariant(variant);
    setFormData({
      bikeId: variant.bikeId || '',
      name: variant.name || '',
      price: variant.price || '',
      image: variant.image || '',
      url: variant.url || ''
    });
    setShowFormModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bikeId || !formData.name) {
      setError('Model and Name are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editingVariant) {
        await bikeVariantAPI.update(editingVariant.id, formData);
      } else {
        await bikeVariantAPI.create(formData);
      }
      setShowFormModal(false);
      setEditingVariant(null);
      fetchVariants(page, limit, search, bikeFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bike variant');
    } finally {
      setFormLoading(false);
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingVariant(null);
  };

  const handleSync = () => {
    setShowSyncModal(true);
    setSyncError('');
    setPreviewVariants([]);
    setSelectedBrandForSync('');
    setSelectedModelForSync('');
    setModelsForSync([]);
    setPreviewBikeInfo(null);
  };

  const handleBrandChangeForSync = async (brandId) => {
    setSelectedBrandForSync(brandId);
    setSelectedModelForSync('');
    setPreviewVariants([]);
    setPreviewBikeInfo(null);

    if (brandId) {
      try {
        const response = await bikeVariantAPI.getModels({ brandId });
        setModelsForSync(response.data || []);
      } catch (err) {
        console.error('Failed to fetch models for sync:', err);
        setModelsForSync([]);
      }
    } else {
      setModelsForSync([]);
    }
  };

  const handlePreviewVariants = async () => {
    if (!selectedModelForSync) {
      setSyncError('Please select a model first');
      return;
    }

    setLoadingPreview(true);
    setSyncError('');
    setPreviewVariants([]);

    try {
      const response = await bikeVariantAPI.preview({ bikeSlug: selectedModelForSync });
      setPreviewVariants(response.data.variants || []);
      setPreviewBikeInfo(response.data.bike || null);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to fetch variants from BikeDekho');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSyncSingle = async (variant) => {
    setSyncingVariant(variant.slug);
    try {
      await bikeVariantAPI.syncSingle({
        variantId: variant.variantId,
        bikeId: variant.bikeId,
        name: variant.name,
        slug: variant.slug,
        price: variant.price,
        url: variant.url
      });
      setPreviewVariants(prev => prev.map(v =>
        v.slug === variant.slug ? { ...v, existsInDb: true } : v
      ));
      fetchVariants(page, limit, search, bikeFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync variant');
    } finally {
      setSyncingVariant(null);
    }
  };

  const handleSyncAll = async () => {
    if (!selectedModelForSync) {
      setSyncError('Please select a model first');
      return;
    }

    setSyncing(true);
    try {
      await bikeVariantAPI.sync({ bikeSlug: selectedModelForSync });
      const response = await bikeVariantAPI.preview({ bikeSlug: selectedModelForSync });
      setPreviewVariants(response.data.variants || []);
      setPage(1);
      fetchVariants(1, limit, search, bikeFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync all variants');
    } finally {
      setSyncing(false);
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setPreviewVariants([]);
    setSyncError('');
    setSelectedBrandForSync('');
    setSelectedModelForSync('');
    setModelsForSync([]);
    setPreviewBikeInfo(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleBikeFilterChange = (value) => {
    setBikeFilter(value);
    setPage(1);
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return price;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Create/Edit Form Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVariant ? 'Edit Bike Variant' : 'Add Bike Variant'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Model *</label>
                  <select
                    name="bikeId"
                    value={formData.bikeId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Model</option>
                    {models.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Variant Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., Drum Brake, Disc Brake, ABS"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleFormChange}
                    placeholder="e.g., Rs. 75,000"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleFormChange}
                    placeholder="e.g., /uploads/bikes/variant.jpg"
                  />
                </div>
                <div className="form-group">
                  <label>Source URL</label>
                  <input
                    type="text"
                    name="url"
                    value={formData.url}
                    onChange={handleFormChange}
                    placeholder="e.g., /bikes/hero/splendor-plus"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingVariant ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Bike Variants</h2>
              <button className="modal-close" onClick={closeSyncModal}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              <div className="sync-brand-selector">
                <label>Select Brand:</label>
                <select
                  value={selectedBrandForSync}
                  onChange={(e) => handleBrandChangeForSync(e.target.value)}
                  className="brand-select"
                >
                  <option value="">-- Select Brand --</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.brandName}</option>
                  ))}
                </select>
              </div>

              {selectedBrandForSync && (
                <div className="sync-brand-selector">
                  <label>Select Model to Sync:</label>
                  <div className="sync-brand-row">
                    <select
                      value={selectedModelForSync}
                      onChange={(e) => setSelectedModelForSync(e.target.value)}
                      className="brand-select"
                    >
                      <option value="">-- Select Model --</option>
                      {modelsForSync.map(model => (
                        <option key={model.id} value={model.slug}>{model.name}</option>
                      ))}
                    </select>
                    <button
                      className="btn btn-primary"
                      onClick={handlePreviewVariants}
                      disabled={!selectedModelForSync || loadingPreview}
                    >
                      {loadingPreview ? 'Loading...' : 'Load Variants'}
                    </button>
                  </div>
                </div>
              )}

              {previewBikeInfo && (
                <div className="preview-brand-header">
                  <h3>Variants for {previewBikeInfo.name}</h3>
                  <span className="model-count">{previewVariants.length} variants found</span>
                </div>
              )}

              {loadingPreview && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading variants from BikeDekho...</p>
                </div>
              )}
              {syncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <h3>Error</h3>
                  <p>{syncError}</p>
                </div>
              )}
              {!loadingPreview && !syncError && previewVariants.length > 0 && (
                <div className="preview-brands-grid preview-models-grid">
                  {previewVariants.map((variant) => (
                    <div key={variant.slug} className="preview-brand-card preview-model-card">
                      <div className="preview-brand-info preview-model-info">
                        <h4>{variant.name}</h4>
                        {variant.price && (
                          <span className="model-price">{variant.price}</span>
                        )}
                      </div>
                      <button
                        className={`btn btn-sm ${variant.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingle(variant)}
                        disabled={syncingVariant === variant.slug}
                      >
                        {syncingVariant === variant.slug ? 'Syncing...' : (variant.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeSyncModal}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAll}
                disabled={syncing || loadingPreview || !selectedModelForSync || previewVariants.length === 0}
              >
                {syncing ? 'Syncing All...' : 'Sync All Variants'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Bike Variants</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Variant
          </button>
          <button
            className="btn btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by variant name..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <SearchableDropdown
              options={models.map(model => ({ value: String(model.id), label: model.name }))}
              value={String(bikeFilter || '')}
              onChange={handleBikeFilterChange}
              placeholder="Search models..."
              allLabel="All Models"
            />
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : variants.length === 0 ? (
          <div className="empty-state">
            <p>No bike variants found. {search || bikeFilter ? 'Try adjusting your filters.' : 'Sync from BikeDekho to get started!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Variant Name</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <tr key={variant.id}>
                    <td>{variant.id}</td>
                    <td>{variant.bike?.brand?.brandName || '-'}</td>
                    <td>{variant.bike?.name || '-'}</td>
                    <td>
                      {variant.name}
                      {/* Retired upstream: absent from the sync preview and can
                          never gain specs, so flag it here — this table is the
                          only place an admin can see these rows at all. */}
                      {variant.variantStatus === 'DISCONTINUED' && (
                        <span
                          title="No longer listed by the data source — specifications cannot be synced"
                          style={{
                            marginLeft: 8, padding: '2px 8px', borderRadius: 10,
                            fontSize: 11, fontWeight: 600,
                            background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap',
                          }}
                        >
                          Discontinued
                        </span>
                      )}
                    </td>
                    <td>{formatPrice(variant.price)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-view"
                          onClick={() => navigate(`/bike-variants/${variant.id}`)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(variant)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(variant.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .form-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .form-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px 24px;
          border-radius: 12px 12px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }
        .form-modal .modal-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
        }
        .form-modal .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .form-modal .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .form-modal .modal-body {
          padding: 24px;
        }
        .form-modal .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
      `}</style>
    </div>
  );
}

export default BikeVariants;
